import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card, Chip, DChip, DataTable, EmptyState, Pane, PaneHead, PhoneCards } from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  REASON_LABEL,
  byActor,
  daysTo,
  dayMonth,
  expiryLabel,
  expiryTone,
  useItemHistory,
  useShelf,
  type ShelfItem,
} from '../stock-manager/lib/useStock';
import { errorText, useTechActions } from './api';
import { TechModal } from './components/TechModal';
import { PhoneScreen } from './components/PhoneScreen';
import { useTechOverlays } from './components/TechOverlays';

type Lane = 'all' | 'low' | 'out';

const NONE: ShelfItem[] = [];

/**
 * Technician · Stock — `g_stock`. THE SHELF, READ-ONLY.
 *
 * The same `GET /stock` the nurse and the Stock manager read (`stock:read`),
 * sorted by soonest expiry. Lanes All / Low / Out of stock, a pane per item
 * (batches, movements), and a flag instead of the manager's write-off controls.
 * The flag reaches the clinic's managers as a line.
 */
export function TechStockPage() {
  const { branch } = useShellIdentity();
  const branchName = branch.replace(/\s*▾\s*$/, '');
  const [lane, setLane] = useState<Lane>('all');
  const [flag, setFlag] = useState<string | null>(null);
  const [pickedId, setPicked] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  usePageMeta({ title: 'Stock' });
  const ov = useTechOverlays();
  const { data: shelf, isLoading, isError } = useShelf();
  const items = shelf ?? NONE;

  const counts = {
    all: items.length,
    low: items.filter((i) => i.low).length,
    out: items.filter((i) => i.inStock <= 0).length,
    expiring: items.filter((i) => i.expiringSoon).length,
  };
  const rows = useMemo(
    () =>
      items
        .filter((i) => (lane === 'low' ? i.low : lane === 'out' ? i.inStock <= 0 : true))
        .filter((i) => {
          const q = query.trim().toLowerCase();
          return !q || i.name.toLowerCase().includes(q) || (i.barcode ?? '').toLowerCase().includes(q);
        })
        .sort(
          (a, b) =>
            (a.nextExpiry ?? '9999-12-31').localeCompare(b.nextExpiry ?? '9999-12-31') ||
            a.name.localeCompare(b.name),
        ),
    [items, lane, query],
  );
  const picked = items.find((i) => i.id === pickedId) ?? null;
  const sub = `${branchName} · ${counts.all} items · ${counts.expiring} expiring · ${counts.low} low — read-only`;
  const flagSeed = picked ? `${picked.name} — ` : '';

  const kind = (i: ShelfItem) =>
    [i.category, i.batchTracked ? 'batch-tracked' : 'no batches'].filter(Boolean).join(' · ');

  const tableRows: TableRow[] = rows.map((i) => {
    const tone = expiryTone(i.nextExpiry);
    return {
      id: i.id,
      cells: {
        item: (
          <div>
            <p className="t-strong">{i.name}</p>
            <p className="t-cap mut">{kind(i)}</p>
          </div>
        ),
        qty: <span className={`t-body w6 num${i.low ? ' amb' : ''}`}>{i.inStock}</span>,
        min: <span className="t-body num">{i.minLevel ?? '—'}</span>,
        exp: !i.batchTracked || !i.nextExpiry ? (
          <span className="t-sec mut">—</span>
        ) : tone ? (
          <Chip kind={tone} small>{expiryLabel(i.nextExpiry)}</Chip>
        ) : (
          <span className="t-sec">{expiryLabel(i.nextExpiry)}</span>
        ),
      },
    };
  });

  useShellChrome(
    {
      raw: true,
      paneOpen: !!picked,
      onPaneClose: () => setPicked(null),
      title: 'Stock',
      sub,
      search: 'Name or barcode…',
      searchValue: query,
      onSearchChange: setQuery,
      onBell: ov.openBell,
      primary: {
        label: counts.low ? `Tell the manager — ${counts.low} low` : 'Tell the manager',
        onClick: () => setFlag(''),
      },
      pane: picked ? (
        <ItemPane item={picked} onFlag={() => setFlag(flagSeed)} />
      ) : (
        <Pane head={<PaneHead overline="Selected item" title="Nothing picked" sub="the shelf, read-only" />}>
          <div className="emptystate">
            <p className="t-sec">Nothing to dock — this panel follows the item you pick.</p>
          </div>
          <div className="card card-acc">
            <p className="t-sec" style={{ color: 'var(--brand-600)' }}>
              Read-only here. The shelf belongs to the stock manager — you can flag it, not change it.
            </p>
            <p className="mt8"><DChip kind="warn" onClick={() => setFlag('')}>Tell the manager — something is low</DChip></p>
          </div>
        </Pane>
      ),
      phone: (
        <PhoneScreen door="stock" title="Stock" sub={`${branchName} · read-only`}>
          <div className="filterrow" style={{ padding: '0 var(--m)' }}>
            {LANES.map(([k, label]) => (
              <Chip key={k} small kind={lane === k ? 'brand' : 'outline'} className={lane === k ? 'w6' : undefined} onClick={() => setLane(k)}>
                {label} · {counts[k]}
              </Chip>
            ))}
          </div>
          <PhoneCards
            columns={[
              { key: 'item', label: 'Item', width: 80 },
              { key: 'exp', label: 'Exp.', width: 20 },
            ]}
            rows={tableRows.map((r, n) => ({
              ...r,
              cells: {
                item: (
                  <div style={{ minWidth: 0 }}>
                    <p className="t-strong">{rows[n].name}</p>
                    <p className={`t-cap ${rows[n].low ? 'amb w6' : 'mut'}`}>
                      {rows[n].inStock} {rows[n].unit || 'pcs'}{rows[n].low ? ' — low' : ''}
                    </p>
                  </div>
                ),
                exp: r.cells.exp,
              },
            }))}
            body="item"
            trailing="exp"
            onOpen={(r) => setPicked(r.id)}
            empty={<EmptyState title={isLoading ? 'Reading the shelf…' : 'Nothing here'} sub="" />}
          />
          <button type="button" className="btn btn-primary mt12" style={{ margin: '12px var(--m)' }} onClick={() => setFlag('')}>
            Tell the manager
          </button>
        </PhoneScreen>
      ),
    },
    [branchName, picked, pickedId, rows, isLoading, lane, counts.low, query],
  );

  return (
    <div className="maie desk tech-root">
      {ov.overlays}
      <div className="filterrow">
        {LANES.map(([k, label]) => (
          <Chip key={k} small kind={lane === k ? 'brand' : 'outline'} className={lane === k ? 'w6' : undefined} onClick={() => setLane(k)}>
            {label} · {counts[k]}
          </Chip>
        ))}
      </div>
      <div className="dcontent">
        <DataTable
          caption="The shelf, soonest expiry first"
          columns={[
            { key: 'item', label: 'Item', width: 46 },
            { key: 'qty', label: 'Qty', width: 12 },
            { key: 'min', label: 'Min', width: 12 },
            { key: 'exp', label: 'Earliest exp.', width: 30 },
          ]}
          rows={tableRows}
          selectedId={pickedId}
          onSelect={(r) => setPicked(r.id === pickedId ? null : r.id)}
          empty={
            <div className="emptystate mt12">
              <p className="es-t">
                {isLoading
                  ? 'Reading the shelf…'
                  : isError
                    ? 'The shelf did not come back.'
                    : lane === 'out'
                      ? 'Nothing is out of stock.'
                      : lane === 'low'
                        ? 'Nothing is low.'
                        : 'The shelf is empty.'}
              </p>
              <p className="mt12"><DChip kind="warn" onClick={() => setFlag('')}>Tell the manager — something is low</DChip></p>
            </div>
          }
        />
        <p className="t-sec sec mt16">
          {lane === 'low'
            ? 'Showing: only what is running low.'
            : lane === 'out'
              ? 'Showing: only what has run out.'
              : 'Showing: the whole shelf, sorted by soonest expiry — the drops and consumables you open daily are the ones to watch.'}
        </p>
      </div>
      <FlagModal key={flag ?? 'closed'} open={flag !== null} initial={flag ?? ''} onClose={() => setFlag(null)} />
    </div>
  );
}

const LANES: Array<['all' | 'low' | 'out', string]> = [['all', 'All'], ['low', 'Low'], ['out', 'Out of stock']];

/** The docked item: stock / min / used per month, batches FEFO, movements. */
function ItemPane({ item, onFlag }: { item: ShelfItem; onFlag: () => void }) {
  const hist = useItemHistory(item.id);
  const lots = item.lots.filter((l) => l.quantity > 0);
  const first = lots[0];
  const days = daysTo(first?.expiresOn ?? null);
  return (
    <Pane
      head={<PaneHead overline="Selected item" title={item.name} wrapTitle
        sub={[item.category, item.batchTracked ? 'batch-tracked' : 'no batches'].filter(Boolean).join(' · ')} />}
      foot={
        <button type="button" className="btn btn-outline grow" style={{ minHeight: 44 }} onClick={onFlag}>
          {item.expiringSoon && first ? `Tell the manager — expiring ${expiryLabel(first.expiresOn)}` : 'Tell the manager'}
        </button>
      }
    >
      <Card>
        <div className="flex" style={{ gap: 24 }}>
          <div><p className="t-title num">{item.inStock}</p><p className="t-cap mut">In stock</p></div>
          <div><p className="t-title num">{item.minLevel ?? '—'}</p><p className="t-cap mut">Min</p></div>
          <div><p className="t-title num">{item.perMonth ?? '—'}</p><p className="t-cap mut">Used / mo</p></div>
        </div>
      </Card>
      {item.expiringSoon && first ? (
        <Card style={{ borderColor: 'var(--danger-600)' }}>
          <p className="t-sec red w6">
            {first.lotCode ? `Batch ${first.lotCode} · ` : ''}
            {days !== null && days <= 0 ? 'already expired — do not use it' : `expires in ${days} d — open this batch first`}
          </p>
        </Card>
      ) : null}
      {item.batchTracked && lots.length ? (
        <>
          <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>Batches — earliest first (FEFO)</p>
          {lots.map((l) => (
            <Card key={l.id}>
              <div className="flex">
                <p className="t-strong grow">
                  {l.lotCode ? `${l.lotCode} · ` : ''}{l.expiresOn ? `exp ${expiryLabel(l.expiresOn)}` : 'no expiry'}
                </p>
                <p className="t-strong">{l.quantity}</p>
              </div>
            </Card>
          ))}
        </>
      ) : null}
      {(hist.data ?? []).length ? (
        <>
          <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>Movements</p>
          {(hist.data ?? []).slice(0, 5).map((m) => (
            <p key={m.id} className="t-sec sec">
              {dayMonth(m.at)} {m.delta > 0 ? `+${m.delta}` : `−${Math.abs(m.delta)}`} · {REASON_LABEL[m.reason] ?? m.reason}
              {byActor(m.actorName)}
            </p>
          ))}
        </>
      ) : null}
      <Card style={{ marginTop: 12 }}>
        <p className="t-sec sec">Read-only here. The shelf belongs to the stock manager — you can flag it, not change it.</p>
      </Card>
    </Pane>
  );
}

function FlagModal({ open, onClose, initial }: { open: boolean; onClose: () => void; initial: string }) {
  const act = useTechActions();
  const [line, setLine] = useState(initial);
  return (
    <TechModal
      open={open}
      onClose={onClose}
      title="Tell the manager"
      sub="a line, not a count — what is low, and anything they should know"
      foot={(
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!line.trim() || act.flagLow.isPending}
            onClick={() => act.flagLow.mutate(line.trim(), {
              onSuccess: () => {
                toast.success('Flagged — the manager has it');
                setLine('');
                onClose();
              },
              onError: (e) => toast.error(errorText(e)),
            })}>
            Send the flag
          </button>
        </>
      )}
    >
      <label className="t-ovl mut" style={{ display: 'block', marginTop: 16 }} htmlFor="tech-flag-line">What is low</label>
      <input id="tech-flag-line" className="tech-inp" value={line} maxLength={300} autoFocus
        placeholder="e.g. tropicamide 1% — two vials left" onChange={(e) => setLine(e.target.value)} />
    </TechModal>
  );
}
